import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import moment from "Helpers/MomentCompat";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";

import QuestionAnswerForm from "../QuestionAnswer";
import QuestionForm from "../QuestionForm";
import * as S from "./styles";

const TYPE_OPTIONS = [
  { value: "text", label: "Texto" },
  { value: "checkbox", label: "Múltipla Escolha" },
];

const UpdateProjectQuestions = ({ projectId }) => {
  const { api } = UseServices();
  const { loading } = UseGlobalLoading();

  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestionAnswer, setSelectedQuestionAnswer] = useState(null);

  const [answers, setAnswers] = useState({});

  /*
   * ⚠️ GUARDA DO `projectId` (24/09/2026). A sub-aba é renderizada no primeiro
   * render do detalhe, quando `selectedProject` ainda é `{}` — logo `projectId`
   * chega `undefined`. Sem a guarda, a busca saía com `project_id: undefined`, o
   * backend recusava ("ID do projeto não informado") e, com deps `[]`, NUNCA
   * havia segunda tentativa: a sessão ficava vazia para sempre.
   *
   * Pelo caminho normal ninguém via, porque a aba padrão é "Documentos" e o
   * projeto já carregou quando o admin clica aqui. Quem expõe é o DEEP LINK
   * `?tab=` — que é justamente o que o e-mail ao admin e a tela de pendências
   * usam.
   */
  useEffect(() => {
    if (projectId) return getProjectQuestions(projectId);

    // eslint-disable-next-line
  }, [projectId]);

  const getProjectQuestions = (project_id) => {
    api
      .post("/project-questions/get-project-questions-by-project-id", {
        project_id: project_id,
      })
      .then((response) => {
        const questions = response.data?.project_questions;

        setQuestions(questions);

        for (const question of questions) {
          if (!question.answers || !question.answers.length) continue;

          const is_text_answer = question.type === "text";

          if (is_text_answer) {
            setAnswers((prev) => ({
              ...prev,
              [question.id]: question?.answers[0]?.text,
            }));
            continue;
          }

          setAnswers((prev) => ({
            ...prev,
            [question.id]: question.answers.map(
              (answer) => answer?.question_option_id,
            ),
          }));
        }
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  return (
    <S.Wrapper>
      {selectedQuestion ? (
        <QuestionForm
          projectId={projectId}
          selectedQuestion={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          getProjectQuestions={getProjectQuestions}
        />
      ) : null}

      {selectedQuestionAnswer ? (
        <QuestionAnswerForm
          selectedQuestion={selectedQuestionAnswer}
          setSelectedQuestion={setSelectedQuestionAnswer}
          answers={answers}
        />
      ) : null}

      <Form>
        <S.Section>
          <span>Perguntas</span>
          <hr />
        </S.Section>

        <S.Buttons>
          <div />

          <Button
            type="button"
            text="Criar Pergunta"
            disabled={loading}
            onClick={() => setSelectedQuestion(true)}
          />
        </S.Buttons>

        {!questions.length ? <span>Nenhuma pergunta cadastrada.</span> : null}

        {questions.map((question) => (
          <S.Quota key={question.id}>
            <S.QuotaInfos>
              <S.Header>
                <h2>{question.text}</h2>
                <span>
                  {TYPE_OPTIONS.find((o) => o.value === question.type)?.label}
                </span>
              </S.Header>
              <span>
                Criada em:{" "}
                <strong>
                  {moment(question.createdAt).format("DD/MM/YYYY")}
                </strong>
              </span>
              <S.Buttons>
                <Button
                  type="button"
                  text="Editar"
                  disabled={loading}
                  onClick={() => setSelectedQuestion(question)}
                />
                {question?.has_answer && (
                  <Button
                    type="button"
                    text="Visualizar Resposta"
                    disabled={loading}
                    onClick={() => setSelectedQuestionAnswer(question)}
                    outline
                  />
                )}
              </S.Buttons>
            </S.QuotaInfos>
          </S.Quota>
        ))}
      </Form>
    </S.Wrapper>
  );
};

export default UpdateProjectQuestions;
